import { useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router'
import { ErrorState, LoadingState } from '@/components/composites/states'
import { cn } from '@/lib/utils'
import { paths } from '@/routes/paths'
import { crm2Api, useCrm2Today, useCrm2Write, type Deal } from './api'
import { DealSheet } from './DealSheet'
import { Crm2Frame, Dot, LEVEL_SOFT, LINK_CHIP, money, Section, shortDay } from './shared'

/** What needs the caller today: their tasks, deals gone quiet, demos waiting for Sales, check-ins and project tasks. */
export function TodayPage() {
  const { workspaceSlug = '' } = useParams()
  const [params, setParams] = useSearchParams()
  const today = useCrm2Today()
  const tick = useCrm2Write(({ id }: { id: string }) => crm2Api.patchActivity(id, { done: true }), () => 'Done')
  const openId = params.get('deal')
  const [now] = useState(() => Date.now())
  const open = (id: string | null) => {
    const next = new URLSearchParams(params)
    if (id) next.set('deal', id)
    else next.delete('deal')
    setParams(next, { replace: true })
  }

  return (
    <Crm2Frame title="Today" description="Your tasks and deals for today, most urgent first. Sales picks up every finished demo within two working days.">
      {today.error ? (
        <ErrorState error={today.error} onRetry={() => void today.refetch()} />
      ) : today.isPending ? (
        <LoadingState label="Loading your day" />
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          <Section title="My tasks" count={today.data.tasks.length} empty="No open tasks.">
            <ul className="divide-y divide-border-soft">
              {today.data.tasks.map((t) => {
                const late = t.dueAt && Date.parse(t.dueAt) < now
                return (
                  <li key={t.id} className="flex items-center gap-2 px-4 py-2 text-sm">
                    <input type="checkbox" aria-label={`Done: ${t.subject}`} onChange={() => tick.mutate({ id: t.id })} />
                    <span className="min-w-0 flex-1 truncate">{t.subject}</span>
                    <Link to={paths.crm2Account(workspaceSlug, t.accountId)} className={LINK_CHIP}>{t.accountName}</Link>
                    <span className={cn('text-xs', late ? 'font-medium text-danger' : 'text-muted-foreground')}>{shortDay(t.dueAt)}</span>
                  </li>
                )
              })}
            </ul>
          </Section>
          <Section title="Demo Done, waiting for Sales" count={today.data.pickup.length} empty="Nothing waiting.">
            <DealList deals={today.data.pickup} onOpen={open} />
          </Section>
          <Section title="My deals needing action" count={today.data.needsAction.length} empty="All your deals have a next step on time.">
            <DealList deals={today.data.needsAction} onOpen={open} />
          </Section>
          <Section title="Nurture check-ins this week" count={today.data.checkIns.length} empty="No check-ins due.">
            <DealList deals={today.data.checkIns} onOpen={open} checkIn />
          </Section>
          <Section title="My TapPM tasks this week" count={today.data.projectTasks.length} empty="No project tasks due.">
            <ul className="divide-y divide-border-soft">
              {today.data.projectTasks.map((t) => (
                <li key={t.id} className="flex items-center gap-2 px-4 py-2 text-sm">
                  <span className="min-w-0 flex-1 truncate">{t.title}</span>
                  <Link to={paths.pm2Project(workspaceSlug, t.projectId)} className={LINK_CHIP}>{t.projectName}</Link>
                  <span className={cn('text-xs', t.dueAt && Date.parse(t.dueAt) < now ? 'font-medium text-danger' : 'text-muted-foreground')}>{shortDay(t.dueAt)}</span>
                </li>
              ))}
            </ul>
          </Section>
          <Section title="My book">
            <p className="px-4 py-3 text-sm">
              <span className="num font-semibold">{today.data.mine.deals}</span> open deals ·{' '}
              <span className="num font-semibold">{money(today.data.mine.salesValue)}</span> open in Sales
            </p>
          </Section>
        </div>
      )}
      <DealSheet id={openId} onClose={() => open(null)} onOpen={open} />
    </Crm2Frame>
  )
}

function DealList({ deals, onOpen, checkIn }: { deals: Deal[]; onOpen: (id: string) => void; checkIn?: boolean }) {
  return (
    <ul className="divide-y divide-border-soft">
      {deals.map((d) => (
        <li key={d.id}>
          <button type="button" onClick={() => onOpen(d.id)} className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm hover:bg-accent">
            <Dot level={d.fresh.level} />
            <span className={cn(LINK_CHIP, 'text-sm')}>{d.accountName}</span>
            <span className="text-xs text-muted-foreground">{d.stageLabel}</span>
            <span className={cn('ml-auto truncate rounded px-1.5 py-0.5 text-tiny', checkIn ? 'bg-muted text-text-secondary' : LEVEL_SOFT[d.fresh.level])}>
              {checkIn ? `Check in ${shortDay(d.checkInAt)}` : (d.fresh.reasons[0] ?? '')}
            </span>
          </button>
        </li>
      ))}
    </ul>
  )
}
